// ============================================
// FILE: frontend/src/components/matching/MatchDetailsModal.jsx
// PURPOSE: Modal to display full match details with landowner/investor info
// FIXED: Added complete details display for both parties
// ============================================

import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Badge } from '../ui/badge';
import { Card, CardContent } from '../ui/card';
import { Separator } from '../ui/separator';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { MapPin, DollarSign, Calendar, Ruler, Briefcase, User, Mail } from 'lucide-react';
import { format } from 'date-fns';

// ========================================
// MAIN COMPONENT
// ========================================

export function MatchDetailsModal({ match, isOpen, onClose }) {
  console.log('[MatchDetailsModal] Rendering with match:', match?.id);
  console.log('[MatchDetailsModal] Full match object:', JSON.stringify(match, null, 2));  // ADD THIS LINE HERE

  if (!match) {
    console.log('[MatchDetailsModal] No match data');
    return null;
  }

  // ========================================
  // HELPER FUNCTIONS
  // ========================================

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending_review':
        return 'bg-yellow-100 text-yellow-800';
      case 'approved':
        return 'bg-blue-100 text-blue-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      case 'connected':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatStatus = (status) => {
    return status
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const formatCurrency = (amount) => {
    if (!amount) return 'N/A';
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES',
      minimumFractionDigits: 0
    }).format(amount);
  };

  // ========================================
  // RENDER
  // ========================================

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <div>
              <DialogTitle className="text-2xl flex items-center gap-2">
                Match Details
                <Badge className="text-lg px-3 py-1 bg-gradient-to-r from-blue-600 to-purple-600">
                  {match.matchScore}% Match
                </Badge>
              </DialogTitle>
              <DialogDescription className="mt-2">
                Created on {format(new Date(match.createdAt), 'PPP')}
              </DialogDescription>
            </div>
            <Badge className={getStatusColor(match.status)}>
              {formatStatus(match.status)}
            </Badge>
          </div>
        </DialogHeader>

        <div className="space-y-6 mt-6">
          {/* ========================================
              SCORE BREAKDOWN
              ======================================== */}
          <Card>
            <CardContent className="pt-6">
              <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                <Briefcase className="w-5 h-5" />
                Score Breakdown
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div className="text-center p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg">
                  <MapPin className="w-6 h-6 mx-auto mb-2 text-blue-600" />
                  <p className="text-xs text-muted-foreground mb-1">Location Match</p>
                  <p className="text-2xl font-bold text-blue-700">
                    {match.scoreBreakdown?.location || 0}<span className="text-sm">/30</span>
                  </p>
                </div>
                
                <div className="text-center p-4 bg-gradient-to-br from-green-50 to-green-100 rounded-lg">
                  <DollarSign className="w-6 h-6 mx-auto mb-2 text-green-600" />
                  <p className="text-xs text-muted-foreground mb-1">Capital Match</p>
                  <p className="text-2xl font-bold text-green-700">
                    {match.scoreBreakdown?.capital || 0}<span className="text-sm">/30</span>
                  </p>
                </div>
                
                <div className="text-center p-4 bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg">
                  <Briefcase className="w-6 h-6 mx-auto mb-2 text-purple-600" />
                  <p className="text-xs text-muted-foreground mb-1">Project Type</p>
                  <p className="text-2xl font-bold text-purple-700">
                    {match.scoreBreakdown?.projectType || 0}<span className="text-sm">/20</span>
                  </p>
                </div>
                
                <div className="text-center p-4 bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg">
                  <Calendar className="w-6 h-6 mx-auto mb-2 text-orange-600" />
                  <p className="text-xs text-muted-foreground mb-1">Timeline Match</p>
                  <p className="text-2xl font-bold text-orange-700">
                    {match.scoreBreakdown?.timeline || 0}<span className="text-sm">/10</span>
                  </p>
                </div>
                
                <div className="text-center p-4 bg-gradient-to-br from-pink-50 to-pink-100 rounded-lg col-span-2">
                  <Ruler className="w-6 h-6 mx-auto mb-2 text-pink-600" />
                  <p className="text-xs text-muted-foreground mb-1">Land Size Match</p>
                  <p className="text-2xl font-bold text-pink-700">
                    {match.scoreBreakdown?.landSize || 0}<span className="text-sm">/10</span>
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* ========================================
              PARTIES DETAILS
              ======================================== */}
          <div className="grid md:grid-cols-2 gap-6">
            {/* LANDOWNER DETAILS */}
            <Card className="border-2 border-green-200 bg-green-50/50">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3 mb-4">
                  <Avatar className="h-12 w-12">
                    <AvatarImage src={match.landowner?.photoURL} />
                    <AvatarFallback className="bg-green-600 text-white">
                      {match.landowner?.fullName?.[0]?.toUpperCase() || 'L'}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <h3 className="font-semibold text-lg text-green-900">Landowner</h3>
                    <p className="text-sm text-green-700">{match.landowner?.user?.fullName || 'N/A'}</p>
                  </div>
                </div>

                <Separator className="my-4" />

                <div className="space-y-3">
                  <div className="flex items-start gap-2">
                    <Mail className="w-4 h-4 text-green-600 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-xs text-muted-foreground">Email</p>
                      <p className="text-sm font-medium break-all">
                        {match.landowner?.user?.email || 'N/A'}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-2">
                    <MapPin className="w-4 h-4 text-green-600 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-xs text-muted-foreground">County</p>
                      <p className="text-sm font-medium">
                        {match.landowner?.county || 'N/A'}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-2">
                    <Ruler className="w-4 h-4 text-green-600 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-xs text-muted-foreground">Land Size</p>
                      <p className="text-sm font-medium">
                        {match.landowner?.landSize 
                        ? `${match.landowner.landSize} acres`
                        : 'N/A'}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-2">
                    <DollarSign className="w-4 h-4 text-green-600 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-xs text-muted-foreground">Capital Needed</p>
                      <p className="text-sm font-medium">
                        {formatCurrency(match.landowner?.capitalNeeded)}
                      </p>
                    </div>
                  </div>
                        
                  <div className="flex items-start gap-2">
                    <Briefcase className="w-4 h-4 text-green-600 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-xs text-muted-foreground">Preferred Project Type</p>
                      <p className="text-sm font-medium">
                        {match.landowner?.developmentType || 'N/A'}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-2">
                    <Calendar className="w-4 h-4 text-green-600 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-xs text-muted-foreground">Timeline</p>
                      <p className="text-sm font-medium">
                        {match.landowner?.timeline || 'N/A'}
                      </p>
                    </div>
                  </div>

                  {match.landowner?.details && (
                    <div className="pt-2">
                      <p className="text-xs text-muted-foreground mb-1">Additional Details</p>
                      <p className="text-sm bg-white p-3 rounded border border-green-200">
                        {match.landowner.details}
                      </p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* INVESTOR DETAILS */}
            <Card className="border-2 border-blue-200 bg-blue-50/50">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3 mb-4">
                  <Avatar className="h-12 w-12">
                    <AvatarImage src={match.investor?.photoURL} />
                    <AvatarFallback className="bg-blue-600 text-white">
                      {match.investor?.fullName?.[0]?.toUpperCase() || 'I'}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <h3 className="font-semibold text-lg text-blue-900">Investor</h3>
                    <p className="text-sm text-blue-700">{match.investor?.user?.fullName || 'N/A'}</p>
                  </div>
                </div>

                <Separator className="my-4" />

                <div className="space-y-3">
                  <div className="flex items-start gap-2">
                    <Mail className="w-4 h-4 text-blue-600 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-xs text-muted-foreground">Email</p>
                      <p className="text-sm font-medium break-all">
                        {match.investor?.user?.email || 'N/A'}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-2">
                      <Ruler className="w-4 h-4 text-blue-600 mt-0.5" />
                      <div className="flex-1">
                        <p className="text-xs text-muted-foreground">Land Size Requirement</p>
                        <p className="text-sm font-medium">
                          {match.investor?.landSizeMin && match.investor?.landSizeMax
                            ? `${match.investor.landSizeMin} - ${match.investor.landSizeMax} acres`
                            : match.investor?.landSizeMin
                          ? `Min ${match.investor.landSizeMin} acres`
                          : match.investor?.landSizeMax
                          ? `Max ${match.investor.landSizeMax} acres`
                          : 'N/A'}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-2">
                    <DollarSign className="w-4 h-4 text-blue-600 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-xs text-muted-foreground">Investment Amount</p>
                      <p className="text-sm font-medium">
                        {formatCurrency(match.investor?.investmentAmount)}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-2">
                    <MapPin className="w-4 h-4 text-blue-600 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-xs text-muted-foreground">Preferred County</p>
                      <p className="text-sm font-medium">
                        {match.investor?.county || 'N/A'}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-2">
                    <Briefcase className="w-4 h-4 text-blue-600 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-xs text-muted-foreground">Preferred Project Type</p>
                      <p className="text-sm font-medium">
                        {match.investor?.projectTypeInterest || 'N/A'}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-2">
                    <Calendar className="w-4 h-4 text-blue-600 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-xs text-muted-foreground">Timeline</p>
                      <p className="text-sm font-medium">
                        {match.investor?.timeline || 'N/A'}
                      </p>
                    </div>
                  </div>

                  {match.investor?.details && (
                    <div className="pt-2">
                      <p className="text-xs text-muted-foreground mb-1">Additional Details</p>
                      <p className="text-sm bg-white p-3 rounded border border-blue-200">
                        {match.investor.details}
                      </p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* ========================================
              ADMIN NOTES
              ======================================== */}
          {match.adminNotes && (
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="pt-6">
                <h3 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
                  <User className="w-4 h-4" />
                  Admin Notes
                </h3>
                <p className="text-sm text-blue-800 whitespace-pre-wrap">
                  {match.adminNotes}
                </p>
              </CardContent>
            </Card>
          )}

          {/* ========================================
              REJECTION REASON
              ======================================== */}
          {match.rejectionReason && match.status === 'rejected' && (
            <Card className="bg-red-50 border-red-200">
              <CardContent className="pt-6">
                <h3 className="font-semibold text-red-900 mb-2">Rejection Reason</h3>
                <p className="text-sm text-red-800 whitespace-pre-wrap">
                  {match.rejectionReason}
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default MatchDetailsModal;